import { makeAutoObservable, ObservableMap } from "mobx";
import { TKey } from "../../../../shared/translations/translationKeys";
import type {
  FlowTemplateData,
  Module,
} from "../../../../templates/generator/types";
import {
  ModuleDoneType,
  ModuleType,
  PlaniraPlanImage,
} from "../../../../templates/types";
import CalendarModuleModel from "../../../modules/Calendar/CalendarModuleModel";
import type { CalendarItemModuleModelData } from "../../../modules/Calendar/data/CalendarItemModel";
import { ModuleDataType } from "../../../modules/core/BaseModuleDataModel";
import { getDefaultDoneType } from "../../../modules/core/BaseModuleModel";
import type { LocationModuleModelData } from "../../../modules/Location/LocationModel";
import LocationModuleModel from "../../../modules/Location/LocationModuleModel";
import type { PlanInfoProps } from "../../../plans/PlanInfoBox";
import { formatDateRange } from "../../dayJs";
import { groupDateRanges } from "../../groupDates";
import { WizardStepDate } from "./WizardStepDate";
import { WizardStepInfo } from "./WizardStepInfo";
import { WizardStepLocation } from "./WizardStepLocation";
import { WizardStepModules } from "./WizardStepModules";
import { WizardStepUser } from "./WizardStepUser";

export enum CreateWizardStep {
  CREATE_USER = "user",
  DATE = "date",
  LOCATION = "location",
  MODULES = "cards",
  INFO = "info",
}

export const CREATE_WIZARD_STEPS = {
  [CreateWizardStep.CREATE_USER]: {
    title: "Create User",
    component: WizardStepUser,
  },
  [CreateWizardStep.DATE]: { title: "Date", component: WizardStepDate },
  [CreateWizardStep.LOCATION]: {
    title: "Location",
    component: WizardStepLocation,
  },
  [CreateWizardStep.MODULES]: {
    title: "What do you want to do",
    component: WizardStepModules,
  },
  [CreateWizardStep.INFO]: {
    title: "Planira info",
    component: WizardStepInfo,
  },
} as const;

type Location = { title: string; latLng: google.maps.LatLngLiteral };

function isCreateWizardStep(step?: string): step is CreateWizardStep {
  return (
    !!step && Object.values(CreateWizardStep).includes(step as CreateWizardStep)
  );
}

type ModuleData =
  | { type: ModuleType.Calendar }
  | { type: ModuleType.Checkboxes }
  | { type: ModuleType.DateDue }
  | { type: ModuleType.Expense }
  | { type: ModuleType.Info }
  | { type: ModuleType.Location }
  | { type: ModuleType.Poll; multipleVotes?: boolean }
  | { type: ModuleType.Rsvp }
  | { type: ModuleType.Tracker };

export type CreateWizardModule = {
  id: string; // unique id for this card
  title: string; // Card title
  emoji: string;
} & ModuleData;

function parseStepFromLocation(): CreateWizardStep {
  const path = window.location.pathname;

  // If your prefix is /plan, strip that off or split differently
  const segments = path
    .replace(/^\/plan\//, "")
    .split("/")
    .filter(Boolean);

  if (segments[0] === "create") {
    const step = segments[1];
    if (isCreateWizardStep(step)) {
      return step;
    }
  }

  return CreateWizardStep.CREATE_USER;
}

type PlanInfo = Omit<PlanInfoProps, "title"> & { title: string };

export class CreateWizardState {
  activeStep: CreateWizardStep;
  title?: string;

  displayName?: string;

  planInfo: PlanInfo = {
    coverUrl: null,
    backgroundColor: null,
    descriptionYooptaEditor: null,
    emoji: "🎉",
    title: "",
  };

  calendarModule?: { dates?: string[]; skip: boolean };
  locationModule?: { location?: Location; skip: boolean };
  modules = new ObservableMap<string, CreateWizardModule>();

  constructor(
    private options: {
      initialDisplayName: string;
      handleNavigate: (step: string, replace?: boolean) => void;
    },
  ) {
    makeAutoObservable(this);

    this.displayName = this.options.initialDisplayName;

    this.activeStep = parseStepFromLocation();

    window.addEventListener("popstate", () => {
      this.activeStep = parseStepFromLocation();
      window.scrollTo({ top: 0, behavior: "instant" });
    });

    if (
      this.activeStep !== CreateWizardStep.CREATE_USER &&
      (!this.calendarModule || !this.locationModule || !this.planInfo.title)
    ) {
      this.navigate(CreateWizardStep.CREATE_USER, true);
    }
  }

  navigate(stepId: CreateWizardStep, replace?: boolean) {
    if (this.activeStep !== stepId) {
      this.activeStep = stepId;
      this.options.handleNavigate(stepId, replace);
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }

  get createSummary() {
    const modules: Module[] = [];

    if (!this.calendarModule?.skip || this.calendarModule?.dates) {
      modules.push({
        type: ModuleType.Calendar,
        doneType: ModuleDoneType.Voting,
        title: this.calendarModule?.dates ? "When" : "When?",
        emoji: "📅",
        settings: {
          groupDays: false,
        },
        onCreated: this.calendarModule?.dates
          ? async (m) => {
              if (CalendarModuleModel.is(m) && this.calendarModule?.dates) {
                const userId = m.userUid;

                if (userId) {
                  await m.addNewData<CalendarItemModuleModelData>(
                    ModuleDataType.CalendarItem,
                    {
                      userId,
                      selectedDates: this.calendarModule.dates,
                    },
                    TKey.ActivityCalendarDatesSet,
                    {
                      dates: groupDateRanges(this.calendarModule.dates.slice())
                        .map((range) => formatDateRange(range, true))
                        .join(", "),
                    },
                  );

                  // Save dates to snapshot
                  m.setDone(this.calendarModule.dates);
                }
              }
            }
          : undefined,
      });
    }

    if (!this.locationModule?.skip || this.locationModule?.location) {
      modules.push({
        type: ModuleType.Location,
        doneType: ModuleDoneType.Voting,
        title: this.locationModule?.location ? "Where" : "Where?",
        emoji: "🌎",
        onCreated: this.locationModule?.location
          ? async (m) => {
              if (LocationModuleModel.is(m) && this.locationModule?.location) {
                const userId = m.userUid;

                if (userId) {
                  const dataItem = await m.addNewData<LocationModuleModelData>(
                    ModuleDataType.Location,
                    {
                      label: this.locationModule.location.title,
                      userId,
                      mapData: this.locationModule.location.latLng,
                    },
                    TKey.ActivityLocationAdded,
                    { label: this.locationModule.location.title },
                  );

                  // Set to done
                  m.setDone([dataItem.uid]);
                }
              }
            }
          : undefined,
      });
    }

    Array.from(this.modules.values()).forEach((mData) => {
      modules.push({
        ...mData,
        doneType: getDefaultDoneType(mData.type),
      });
    });

    const planFlowTemplate: FlowTemplateData = {
      type: "wizard",
      slug: "NOT_USED",

      description: "", // Not the same as planinfo desc...
      longDescription: "", // Only used in Gatsby...
      titleHelp: "",
      imageType: PlaniraPlanImage.Party,

      suggestedTitles: [],

      modules,

      // For PlanInfoBox
      ...this.planInfo,

      onTemplateComplete: {
        // TODO - make optional
        outroTitle: "",
        outroDescription: "",
      },
    };

    return { planFlowTemplate, userDisplayName: this.displayName };
  }

  getActiveStep() {
    if (isCreateWizardStep(this.activeStep)) {
      return CREATE_WIZARD_STEPS[this.activeStep];
    }

    return CREATE_WIZARD_STEPS[CreateWizardStep.CREATE_USER];
  }

  getActiveStepIndex(): number {
    const index = Object.keys(CREATE_WIZARD_STEPS).indexOf(this.activeStep);

    return index === -1 ? 0 : index;
  }

  setActiveStepId(newId: CreateWizardStep) {
    this.navigate(newId);
  }

  setPlanInfo(data: Partial<PlanInfo>) {
    this.planInfo = { ...this.planInfo, ...data };
  }

  setDisplayName(newDisplayName: string) {
    this.displayName = newDisplayName;
  }

  setCalendarModule(data?: { dates?: string[]; skip: boolean }) {
    this.calendarModule = data;
  }

  setLocationModule(data?: { location?: Location; skip: boolean }) {
    this.locationModule = data;
  }

  toggleModule(module: CreateWizardModule) {
    const alreadyAdded = this.modules.has(module.id);
    if (alreadyAdded) {
      this.modules.delete(module.id);
    } else {
      this.modules.set(module.id, module);
    }
  }
}
